/**
 * The selected Client, for the session.
 *
 * Workforce users address Clients one at a time (design §6: the switcher
 * in the shell, persisted in `sessionStorage`). "No Client selected" is a
 * real state, not a null guard: Overview renders the cross-Client summary
 * and the other screens ask for a selection.
 *
 * Since task 208 (Story 48) the selection is a node of the organization
 * tree: a Client, an MSP ("All of Acme", which the All-Clients screens pass
 * to the API as `?org=`), or nothing, meaning everything under the caller's
 * home. It is stored as `bsmdr.selectedNode` (`client:<id>` or `org:<id>`)
 * and can be set by a `?client=` or `?org=` deep link.
 *
 * This is display state only. The API decides which Clients a caller may
 * address; picking one here that the server refuses simply produces a 403
 * on the screen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listOrgs, listTenants, type ClientTenant, type OrgNode } from '@bluesphere/api-client';

import { indexOrgs, type OrgIndex } from '../lib/orgs';
import { useAccessToken } from './useAccessToken';

const STORAGE_KEY = 'bsmdr.selectedNode';
/** Before task 208: a Client id alone. Read once, then replaced. */
const LEGACY_KEY = 'bsmdr.selectedClient';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Selection = { kind: 'client' | 'org'; id: string } | null;

interface SelectedTenantState {
  /** The selected Client, when a Client is selected. */
  tenantId: string | null;
  selected: ClientTenant | null;
  /** The selected organization (an MSP, or the root), when one is. Never the caller's home: that is "nothing selected". */
  orgId: string | null;
  selectedOrg: OrgNode | null;
  tenants: ClientTenant[];
  /** The caller's visible tree; `homeId` null before the tree exists. */
  orgs: OrgIndex;
  isLoading: boolean;
  error: unknown;
  setTenantId: (id: string | null) => void;
  setOrgId: (id: string | null) => void;
}

const SelectedTenantContext = createContext<SelectedTenantState | null>(null);

function parse(raw: string | null): Selection {
  if (!raw) return null;
  const [kind, id] = raw.split(':', 2);
  if ((kind === 'client' || kind === 'org') && id && UUID_RE.test(id)) return { kind, id };
  return null;
}

function readStored(): Selection {
  try {
    const stored = parse(window.sessionStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
    const legacy = window.sessionStorage.getItem(LEGACY_KEY);
    if (legacy && UUID_RE.test(legacy)) return { kind: 'client', id: legacy };
  } catch {
    /* blocked storage */
  }
  return null;
}

function writeStored(selection: Selection): void {
  try {
    window.sessionStorage.removeItem(LEGACY_KEY);
    if (selection) window.sessionStorage.setItem(STORAGE_KEY, `${selection.kind}:${selection.id}`);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private windows and blocked storage: the selection just does not persist.
  }
}

const CLIENT_PARAM = 'client';
const ORG_PARAM = 'org';

/**
 * A deep link may name the Client (`?client=<id>`), the way the notification
 * email does: without it, a link to `/alerts/BSM-2` opened in a fresh
 * session lands on "Select a Client". The parameter wins over the stored
 * selection, is persisted like a switcher choice, and is stripped from the
 * address bar so a copied URL does not keep re-selecting. The API still
 * decides whether the caller may see that Client; an id outside the
 * allow-list is dropped by the effect below when the list loads.
 */
function readInitial(): Selection {
  try {
    const url = new URL(window.location.href);
    for (const [param, kind] of [
      [CLIENT_PARAM, 'client'],
      [ORG_PARAM, 'org'],
    ] as const) {
      const fromLink = url.searchParams.get(param);
      if (fromLink && UUID_RE.test(fromLink)) {
        url.searchParams.delete(param);
        window.history.replaceState(window.history.state, '', url.toString());
        const selection = { kind, id: fromLink.toLowerCase() };
        writeStored(selection);
        return selection;
      }
    }
  } catch {
    /* fall through to the stored selection */
  }
  return readStored();
}

export function SelectedTenantProvider({ children }: { children: ReactNode }) {
  const idToken = useAccessToken();
  const [selection, setSelection] = useState<Selection>(readInitial);
  const tenantId = selection?.kind === 'client' ? selection.id : null;
  const orgId = selection?.kind === 'org' ? selection.id : null;

  // With the platform-operations tenant for a scope-`all` caller (task 200),
  // so an operations alert opened from the all-Clients queue can be
  // selected rather than cleared by the effect below. Its own key: the
  // screens that share `['tenants']` manage Clients and must never see it.
  // Their invalidations still refresh this one, by prefix.
  const tenantsQuery = useQuery({
    queryKey: ['tenants', 'with-operations'],
    queryFn: async () => (await listTenants(idToken, { includeOperations: true })).tenants,
    enabled: Boolean(idToken),
    refetchInterval: 30_000,
  });

  // The caller's part of the organization tree (Story 48). Empty before
  // the tree exists, and then the switcher is the flat Client list.
  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => listOrgs(idToken),
    enabled: Boolean(idToken),
    refetchInterval: 60_000,
  });

  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const orgs = useMemo(
    () => indexOrgs(orgsQuery.data?.home_org_id ?? null, orgsQuery.data?.orgs ?? []),
    [orgsQuery.data],
  );

  const select = useCallback((next: Selection) => {
    setSelection(next);
    writeStored(next);
  }, []);

  // A stored selection the caller can no longer see (revoked, moved out of
  // their subtree, or their scope changed) clears itself rather than
  // pinning every screen to a 403.
  useEffect(() => {
    if (tenantId && tenantsQuery.data) {
      if (!tenantsQuery.data.some((t) => t.client_tenant_id === tenantId)) select(null);
    }
    if (orgId && orgsQuery.data) {
      if (!orgs.byId.has(orgId) || orgId === orgs.homeId) select(null);
    }
  }, [tenantId, orgId, tenantsQuery.data, orgsQuery.data, orgs, select]);

  const setTenantId = useCallback(
    (id: string | null) => select(id ? { kind: 'client', id } : null),
    [select],
  );
  // The home is everything the caller sees: selecting it is selecting nothing.
  const setOrgId = useCallback(
    (id: string | null) => select(id && id !== orgs.homeId ? { kind: 'org', id } : null),
    [select, orgs.homeId],
  );

  const value = useMemo<SelectedTenantState>(
    () => ({
      tenantId,
      selected: tenants.find((t) => t.client_tenant_id === tenantId) ?? null,
      orgId,
      selectedOrg: orgId ? (orgs.byId.get(orgId) ?? null) : null,
      tenants,
      orgs,
      isLoading: tenantsQuery.isLoading,
      error: tenantsQuery.error,
      setTenantId,
      setOrgId,
    }),
    [
      tenantId,
      orgId,
      tenants,
      orgs,
      tenantsQuery.isLoading,
      tenantsQuery.error,
      setTenantId,
      setOrgId,
    ],
  );

  return <SelectedTenantContext.Provider value={value}>{children}</SelectedTenantContext.Provider>;
}

export function useSelectedTenant(): SelectedTenantState {
  const ctx = useContext(SelectedTenantContext);
  if (!ctx) throw new Error('useSelectedTenant must be used inside SelectedTenantProvider');
  return ctx;
}
